import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReferredWorkers } from "../hooks/useReferredWorkers";

const PAGE_SIZE = 10;
const ALL = "all";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const NOW = new Date();
const CURRENT_YEAR = String(NOW.getFullYear());
const CURRENT_MONTH = NOW.getMonth() + 1; 

const YEAR_COUNT = 6;
const buildYearOptions = () => {
  const y = NOW.getFullYear();
  return Array.from({ length: YEAR_COUNT }, (_, i) => String(y - i));
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5d43e2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const CardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5d43e2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const AdminMyReferralsContent = ({ token, userId, workerType = "admin" }) => {
  const navigate = useNavigate();

  const yearOptions = useMemo(buildYearOptions, []);
  const [selectedYear, setSelectedYear] = useState(ALL);
  const [selectedMonthNum, setSelectedMonthNum] = useState(ALL); 

  const maxMonth =
    selectedYear === ALL || selectedYear === CURRENT_YEAR ? CURRENT_MONTH : 12;

  const monthParam =
    selectedYear !== ALL && selectedMonthNum !== ALL ? `${selectedYear}-${selectedMonthNum}` : undefined;

  const yearParam = selectedYear !== ALL && selectedMonthNum === ALL ? selectedYear : undefined;
  const isFiltered = selectedYear !== ALL;

  const handleMonthChange = (e) => {
    const m = e.target.value;
    setSelectedMonthNum(m);
    if (m === ALL) setSelectedYear(ALL);
    else if (selectedYear === ALL) setSelectedYear(CURRENT_YEAR);
  };

  const handleYearChange = (e) => {
    const y = e.target.value;
    setSelectedYear(y);
    if (y === ALL) {
      setSelectedMonthNum(ALL); 
    } else if (y === CURRENT_YEAR && selectedMonthNum !== ALL && Number(selectedMonthNum) > CURRENT_MONTH) {
      setSelectedMonthNum(ALL); 
    }
  };
  const [searchInput, setSearchInput] = useState("");
  const [searchName, setSearchName] = useState(""); 
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchName(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const overall = useReferredWorkers({ token, userId, workerType });

  const monthData = useReferredWorkers({
    token,
    userId,
    workerType,
    month: searchName ? undefined : monthParam,
    year: searchName ? undefined : yearParam,
    name: searchName || undefined,
  });

  const { allReferrals, initialLoading, error } = monthData;

  const monthReferralCount = monthData.totalReferrals ?? allReferrals.length;
  const monthEarnings =
    monthData.totalEarning ?? allReferrals.reduce((sum, r) => sum + (Number(r.earning) || 0), 0);

  const overallReferrals = overall.totalReferrals ?? overall.allReferrals.length;
  const overallEarnings =
    overall.totalEarning ?? overall.allReferrals.reduce((sum, r) => sum + (Number(r.earning) || 0), 0);

  const visible = allReferrals.slice(0, visibleCount);
  const hasMore = visibleCount < allReferrals.length;

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [selectedYear, selectedMonthNum, searchName]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setVisibleCount((c) => c + PAGE_SIZE);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, visibleCount, initialLoading]);

  return (
    <div className="min-h-screen bg-[#fafafc] pt-20 pb-8 text-gray-900 font-sans">
      <div className="max-w-md mx-auto px-3.5">
        {/* Header */}
        <div className="relative flex items-center justify-center mb-3">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go back"
            className="absolute left-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-900 hover:bg-gray-200 transition-colors"
          >
            <ChevronLeft />
          </button>
          <h1 className="text-lg font-bold text-gray-900">My Referrals</h1>
        </div>

        {/* Compact Summary Box */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm px-3 py-2.5 flex items-center mb-3">
          {/* Left Side */}
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-[#ede8ff] flex items-center justify-center shrink-0">
              <UsersIcon />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] text-gray-500 leading-none mb-0.5">Total Referrals</p>
              <p className="text-base font-bold text-[#0fa958] leading-none">
                {overall.initialLoading ? "–" : overallReferrals}
              </p>
            </div>
          </div>

          <div className="w-px h-7 bg-gray-200 mx-2" />

          {/* Right Side */}
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-[#ede8ff] flex items-center justify-center shrink-0">
              <CardIcon />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] text-gray-500 leading-none mb-0.5">Total Earnings</p>
              <p className="text-base font-bold text-[#0fa958] leading-none truncate">
                {overall.initialLoading ? "–" : formatINR(overallEarnings)}
              </p>
            </div>
          </div>
        </div>

        {/* Search by name */}
        <div className="relative mb-2">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name"
            aria-label="Search referrals by name"
            className="w-full bg-white text-[#1A1763] placeholder-gray-400 text-sm rounded-lg border border-gray-300 pl-9 pr-8 py-1.5 focus:outline-none focus:border-2 focus:border-[#f2a200] transition-colors"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        {/* Month + Year Filters */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div className="relative">
            <select
              value={selectedMonthNum}
              onChange={handleMonthChange}
              disabled={Boolean(searchName)}
              aria-label="Filter by month"
              className="w-full appearance-none disabled:opacity-60 disabled:cursor-not-allowed bg-white text-[#1A1763] text-sm rounded-lg border border-gray-300 px-3 py-1.5 pr-8 focus:outline-none focus:border-2 focus:border-[#f2a200] transition-colors"
            >
              <option value={ALL}>All months</option>
              {MONTHS.slice(0, maxMonth).map((name, i) => (
                <option key={name} value={String(i + 1).padStart(2, "0")}>
                  {name}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-600">
              <ChevronDown />
            </span>
          </div>

          <div className="relative">
            <select
              value={selectedYear}
              onChange={handleYearChange}
              disabled={Boolean(searchName)}
              aria-label="Filter by year"
              className="w-full appearance-none disabled:opacity-60 disabled:cursor-not-allowed bg-white text-[#1A1763] text-sm rounded-lg border border-gray-300 px-3 py-1.5 pr-8 focus:outline-none focus:border-2 focus:border-[#f2a200] transition-colors"
            >
              <option value={ALL}>All years</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-600">
              <ChevronDown />
            </span>
          </div>
        </div>

        {/* Month Totals */}
        <div className="flex justify-between items-center px-1 mb-3 text-sm">
          <p className="font-semibold text-slate-500">
            Referrals :{" "}
            <span className="font-bold text-[#1A1763] text-base ml-0.5">
              {initialLoading ? "–" : monthReferralCount}
            </span>
          </p>
          <p className="font-semibold text-slate-500">
            Earnings :{" "}
            <span className="font-bold text-[#1A1763] text-base ml-0.5">
              {initialLoading ? "–" : formatINR(monthEarnings)}
            </span>
          </p>
        </div>

        {initialLoading && (
          <div className="flex justify-center py-6">
            <div className="w-7 h-7 border-[3px] border-gray-200 border-t-[#5d43e2] rounded-full animate-spin" />
          </div>
        )}

        {error && <p className="text-center text-red-600 text-xs my-4">{error}</p>}

        {!initialLoading && !error && (
          <>
            {allReferrals.length === 0 && (
              <p className="text-center text-gray-400 text-xs my-8">
                {searchName
                  ? `No referrals found for "${searchName}".`
                  : !isFiltered
                  ? "No referrals yet."
                  : "No referrals for this period."}
              </p>
            )}

            {/* Referral Cards - Increased vertical height slightly using px-3.5 py-4 */}
            {visible.map((ref, index) => (
              <div
                key={ref._id || index}
                className="bg-white rounded-xl border border-gray-100 shadow-sm px-3.5 py-4 mb-3"
              >
                {/* Card Header */}
                <div className="flex justify-between items-center pb-2.5 border-b border-gray-100">
                  <p className="text-xs font-bold text-[#1A1763]">
                    <span className="font-normal text-gray-500 mr-1">{index + 1}.</span>
                    {ref.name || "Unknown"}
                  </p>
                  <p className="text-xs font-medium text-gray-600">{ref.phone ? `+${ref.phone}` : "—"}</p>
                </div>

                {/* Card Grid Details */}
                <div className="grid grid-cols-[1.2fr_0.9fr_0.9fr] gap-1 pt-3">
                  <div>
                    <p className="text-[11px] font-medium text-gray-500">Joined At</p>
                    <p className="text-xs font-bold text-[#1A1763] mt-1.5 whitespace-nowrap">
                      {formatDate(ref.onBoardingDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-gray-500">Shifts Done</p>
                    <p className="text-xs font-bold text-[#1A1763] mt-1.5">
                      {ref.shift ?? 0}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] font-medium text-gray-500">Referral Earned</p>
                    <p className="text-xs font-bold text-[#1A1763] mt-1.5 whitespace-nowrap">
                      {formatINR(ref.earning)}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Sentinel */}
            <div ref={sentinelRef} className="min-h-[20px]">
              {!hasMore && allReferrals.length > 0 && (
                <p className="text-center text-gray-400 text-[11px] my-2">You've seen all referrals</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMyReferralsContent;